import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Button} from '@tickchak/react-share/app';
import * as css from './LecturersPage.css';
import PageWrapper from '../../components/pageWrapper/PageWrapper';
import LecturerCard from './lecturerCard/LecturerCard';
import PreviousLecturerCard from './previousLecturerCard/PreviousLecturerCard';
import LecturerCardSkeleton from './lecturerCardSkeleton/LecturerCardSkeleton';
import AddLecturerFrame from './addLecturerFrame/AddLecturerFrame';
import AddLecturerPopup from '../components/manageLecturePopup/fields/AddLecturerPopup/AddLecturerPopup';
import LecturersEmpty from './lecturersEmpty/LecturersEmpty';
import SearchFilterBar from '../../../component/searchFilterBar/SearchFilterBar';
import useShow from '../../../hooks/useShow';
import {RootState} from '../../../redux/store.type';
import {TLecturer} from '../../../redux/timetable/timetable.type';
import {getLecturers, getTimetable, updateLecturer} from '../../../redux/timetable/timetableActions';
import PlusThin from '../../../assets/icons/PlusThin';
import DownloadReport from '../../../assets/icons/DownloadReport';
import ViewToggle from '../../../assets/icons/ViewToggle';
import Bell from '../../../assets/icons/bell';

function LecturersPage() {
    const {t} = useTranslation('timetable');
    const eid       = useSelector((s: RootState) => s.event.eid);
    const lecturers = useSelector((s: RootState) => s.timetable.lecturers);
    const lectures  = useSelector((s: RootState) => s.timetable.lectures);
    const getLecturersXHR = useSelector((s: RootState) => s.timetable.getLecturersXHR);
    const addLecturerShow = useShow();
    const editShow = useShow();
    const [editLecturer, setEditLecturer] = useState<TLecturer | undefined>(undefined);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if(!eid) return;
        if(!getLecturersXHR) getLecturers().then();
        getTimetable();
    }, [eid, getLecturersXHR]);

    const isLoading = !getLecturersXHR || getLecturersXHR === 'LOADING';

    const matches = (l: TLecturer) => `${l.firstName} ${l.lastName}`.toLowerCase().includes(search.trim().toLowerCase());
    const eventLecturers    = lecturers.filter(l =>  l.inEvent).filter(matches);
    const previousLecturers = lecturers.filter(l => !l.inEvent).filter(matches);
    const hasResults = eventLecturers.length > 0 || previousLecturers.length > 0;

    const buttons = [
        {key: 0, children: <ViewToggle />, onClick: () => {}, disabled: true, withoutPlus: true, variant: 'subtle'},
        {key: 1, children: <Bell />, text: t('sendNotifications'), onClick: () => {}, disabled: true, withoutPlus: true, variant: 'neutral'},
        {key: 2, children: <DownloadReport />, text: t('detailedReport'), onClick: () => {}, disabled: true, withoutPlus: true, variant: 'neutral'},
        {key: 'divider', Comp: () => <css.toolbarDivider />},
        {key: 3, children: <PlusThin />, text: t('addNewLecturer'), onClick: addLecturerShow.open, withoutPlus: true, variant: 'brand'},
    ];
    const mobileActions = [
        {key: 0, text: t('toggleView'), onClick: () => {}, disabled: true, withoutPlus: true},
        {key: 1, text: t('sendNotifications'), onClick: () => {}, disabled: true, withoutPlus: true},
        {key: 2, text: t('detailedReport'), onClick: () => {}, disabled: true, withoutPlus: true},
        {key: 3, text: t('addNewLecturer'), onClick: addLecturerShow.open, withoutPlus: true},
    ];

    const assignedLids = new Set(
        Object.values(lectures).flat().flatMap(item => item.lecturers?.map(l => l.lid) ?? [])
    );

    if(!isLoading && lecturers.length === 0) {
        return (
            // @ts-expect-error PageWrapper is a JS component without TS types
            <PageWrapper>
                <css.container>
                    <css.sectionTitle>{t('manageLecturers')}</css.sectionTitle>
                    <LecturersEmpty search={search} onAdd={addLecturerShow.open} />
                    {addLecturerShow.isOpen && <AddLecturerPopup show={addLecturerShow} />}
                </css.container>
            </PageWrapper>
        );
    }

    return (
        // @ts-expect-error PageWrapper is a JS component without TS types
        <PageWrapper operations={{buttons, isNewButton: true}} mobileActions={mobileActions}>
            <css.container>
                <SearchFilterBar search={search} onSearch={setSearch} dataCy="lecturers" />
                {!isLoading && !hasResults
                    ? <LecturersEmpty search={search} onAdd={addLecturerShow.open} />
                    : <>
                        <css.grid>
                            {isLoading
                                ? Array.from({length: 4}, (_, i) => <LecturerCardSkeleton key={i} />)
                                : <>
                                    {eventLecturers.map(l => (
                                        <LecturerCard
                                            key={l.lid}
                                            lecturer={l}
                                            isAssignedToLecture={assignedLids.has(l.lid)}
                                            onEdit={() => { setEditLecturer(l); editShow.open(); }}
                                        />
                                    ))}
                                    <AddLecturerFrame onClick={addLecturerShow.open} />
                                </>}
                        </css.grid>

                        <css.addPillWrap>
                            <Button
                                variant="brand"
                                size="category"
                                iconStart={<PlusThin color="black" />}
                                onClick={addLecturerShow.open}
                                data-cy="add-lecturer-pill"
                            >
                                {t('addNewLecturer')}
                            </Button>
                        </css.addPillWrap>

                        <css.divider />

                        <css.previousHeader>
                            <css.sectionTitle>{t('previousLecturers')}</css.sectionTitle>
                        </css.previousHeader>

                        <css.previousGrid>
                            {isLoading
                                ? Array.from({length: 3}, (_, i) => <LecturerCardSkeleton key={i} variant="previous" />)
                                : previousLecturers.map(l => (
                                    <PreviousLecturerCard
                                        key={l.lid}
                                        lecturer={l}
                                        onAssign={() => updateLecturer(l.lid)}
                                    />
                                ))}
                        </css.previousGrid>
                    </>}
                {addLecturerShow.isOpen && <AddLecturerPopup show={addLecturerShow} />}
                {editShow.isOpen && <AddLecturerPopup show={editShow} lecturer={editLecturer} />}
            </css.container>
        </PageWrapper>
    );
}

export default LecturersPage;
